'use client';
import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';

const credentials = [
  { label: 'Certified Professional Résumé Writers' },
  { label: '3,000+ Job Seekers Placed' },
  { label: '15+ Years Career Coaching' },
  { label: 'Trusted by Professionals Worldwide' },
];

const clientLogos = [
  'Microsoft', 'Google', 'Amazon', 'Apple', 'PayPal', 'Deloitte', 'Exxon', 'Uber',
];

function useCountUp(target: number, duration: number, start: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

export default function HeroSection() {
  const [visible, setVisible] = useState(false);
  const [statsStarted, setStatsStarted] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  const resumes = useCountUp(3000, 2200, statsStarted);
  const years = useCountUp(15, 1800, statsStarted);
  const successRate = useCountUp(94, 2000, statsStarted);
  const industries = useCountUp(40, 1900, statsStarted);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setStatsStarted(true);
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <section className="hero-bg relative min-h-screen flex flex-col justify-center" id="hero">
        <div className="noise-overlay" />
        <div className="hero-scan" />

        {/* Subtle dot grid */}
        <div
          className="absolute inset-0 pointer-events-none opacity-5"
          style={{
            backgroundImage: 'radial-gradient(rgba(201,168,76,0.6) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse at 50% 50%, black, transparent 80%)',
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-36 pb-20">

          {/* Eyebrow */}
          <div
            className="flex items-center gap-3 mb-8"
            style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.6s ease 0.2s' }}
          >
            <span className="font-mono-custom text-xs tracking-widest uppercase" style={{ color: 'rgba(201,168,76,0.7)' }}>
              Professional Career Coaching &amp; Résumé Writing
            </span>
            <div className="gold-rule" />
          </div>

          {/* Main headline */}
          <h1
            className="font-fraunces font-light mb-6"
            style={{
              fontSize: 'clamp(3rem, 8vw, 7.5rem)',
              lineHeight: '0.92',
              letterSpacing: '-0.03em',
              color: '#F5F0E8',
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.9s ease 0.4s, transform 0.9s ease 0.4s',
            }}
          >
            Transform Your<br />
            <span style={{ color: '#C9A84C', fontStyle: 'italic' }}>Career.</span>{' '}
            <span style={{ color: '#F5F0E8' }}>Definitively.</span>
          </h1>

          {/* Gold rule */}
          <div className="mt-6 mb-8 gold-rule-full opacity-30" style={{ opacity: visible ? 0.3 : 0, transition: 'opacity 0.6s ease 0.8s' }} />

          {/* Credential strip */}
          <div
            className="flex flex-wrap gap-x-6 gap-y-2 mb-8"
            style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.6s ease 0.9s' }}
          >
            {credentials.map((c, i) => (
              <span key={i} className="flex items-center gap-2 text-sm font-medium" style={{ color: 'rgba(245,240,232,0.7)' }}>
                <span style={{ color: '#C9A84C' }}>·</span>
                {c.label}
              </span>
            ))}
          </div>

          {/* Subline */}
          <p
            className="max-w-xl text-lg font-light leading-relaxed mb-10"
            style={{
              color: 'rgba(245,240,232,0.65)',
              opacity: visible ? 1 : 0,
              transition: 'opacity 0.8s ease 1s',
            }}
          >
            Betty Berkely and her team of career professionals bring deep expertise in résumé writing, job search strategy, and career coaching, helping job seekers at every level land the roles they deserve.
          </p>

          {/* CTAs */}
          <div
            className="flex flex-wrap items-center gap-5 mb-16"
            style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.8s ease 1.2s' }}
          >
            <a href="/packages" className="cta-gold px-8 py-4 rounded-sm text-sm font-semibold tracking-wide inline-flex items-center gap-2">
              View Packages
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
            <a href="/contact" className="cta-secondary text-sm font-medium">
              Free Résumé Critique →
            </a>
          </div>

          {/* Stats counters */}
          <div ref={statsRef} className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 pt-8 border-t" style={{ borderColor: 'rgba(201,168,76,0.15)' }}>
            {[
              { value: resumes, suffix: '+', label: 'Résumés Written' },
              { value: years, suffix: '+', label: 'Years of Career Coaching' },
              { value: successRate, suffix: '%', label: 'Client Interview Success Rate' },
              { value: industries, suffix: '+', label: 'Industries Served' },
            ].map((stat, i) => (
              <div key={i} className="stat-item stat-visible">
                <div className="proof-number mb-1" style={{ fontSize: 'clamp(2.2rem, 4vw, 3.5rem)' }}>
                  {stat.value}{stat.suffix}
                </div>
                <div className="proof-label text-xs leading-snug max-w-[160px]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Client logos strip */}
        <div
          className="relative z-10 border-t py-6"
          style={{ borderColor: 'rgba(201,168,76,0.1)', background: 'rgba(0,0,0,0.2)' }}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <p className="text-xs font-mono-custom mb-4 text-center" style={{ color: 'rgba(201,168,76,0.5)', letterSpacing: '0.12em' }}>
              OUR CLIENTS HAVE LANDED AT
            </p>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
              {clientLogos.map((logo) => (
                <span key={logo} className="text-sm font-semibold tracking-wide" style={{ color: 'rgba(245,240,232,0.35)' }}>
                  {logo}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom fade */}
        <div
          className="absolute bottom-0 inset-x-0 h-24 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, transparent, rgba(74,25,66,0.6))' }}
        />
      </section>

      {/* Betty Berkely Introduction Section */}
      <section className="section-cream relative py-24 lg:py-32 overflow-hidden" id="about-betty">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Photo */}
            <div className="relative flex justify-center lg:justify-start">
              <div
                className="relative rounded-sm overflow-hidden"
                style={{
                  width: '100%',
                  maxWidth: '460px',
                  aspectRatio: '3/4',
                  boxShadow: '0 32px 80px rgba(74,25,66,0.18)',
                  border: '1px solid rgba(201,168,76,0.2)',
                }}
              >
                <Image
                  src="/assets/images/images_imgupscaler.ai_Sharpener_2K-1786377975788.jpg"
                  alt="Betty Berkely, Lead Career Coach and Founder of Betty Berkely Career Coaching"
                  fill
                  style={{ objectFit: 'cover', objectPosition: 'top center' }}
                  priority
                />
                {/* Gold accent overlay bottom */}
                <div
                  className="absolute bottom-0 inset-x-0 h-24 pointer-events-none"
                  style={{ background: 'linear-gradient(to top, rgba(74,25,66,0.5), transparent)' }}
                />
              </div>
              {/* Decorative gold border offset */}
              <div
                className="absolute -bottom-4 -right-4 w-full max-w-[460px] h-full rounded-sm pointer-events-none hidden lg:block"
                style={{
                  border: '2px solid rgba(201,168,76,0.25)',
                  borderRadius: '2px',
                  zIndex: -1,
                }}
              />
            </div>

            {/* Bio content */}
            <div>
              <span className="font-mono-custom text-xs tracking-widest uppercase block mb-5" style={{ color: 'rgba(74,25,66,0.5)' }}>
                Meet Your Lead Coach
              </span>
              <h2
                className="font-fraunces font-medium mb-6"
                style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)', color: '#4A1942', lineHeight: '1.05' }}
              >
                Betty Berkely
              </h2>
              <div className="gold-rule-full opacity-20 mb-8" />
              <p className="text-base leading-relaxed mb-5" style={{ color: '#4A4A4A' }}>
                Betty Berkely is a seasoned career coach and certified professional résumé writer with over 15 years of hands-on experience helping job seekers navigate competitive job markets and secure meaningful roles.
              </p>
              <p className="text-base leading-relaxed mb-5" style={{ color: '#4A4A4A' }}>
                Backed by a dedicated team of career professionals, Betty's practice is built on one core belief: every job seeker deserves a compelling, authentic story that opens doors. From entry-level candidates to senior executives, her team crafts résumés and career strategies that get results.
              </p>
              <p className="text-base leading-relaxed mb-10" style={{ color: '#4A4A4A' }}>
                With a deep understanding of what hiring managers and recruiters look for, Betty and her team have helped thousands of professionals across 40+ industries land interviews, negotiate offers, and build careers they're proud of.
              </p>

              {/* Mini stats */}
              <div className="grid grid-cols-3 gap-6 pt-8 border-t" style={{ borderColor: 'rgba(74,25,66,0.12)' }}>
                {[
                  { value: '3,000+', label: 'Clients Served' },
                  { value: '15+', label: 'Years Experience' },
                  { value: '40+', label: 'Industries' },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="font-fraunces font-semibold text-2xl mb-1" style={{ color: '#4A1942' }}>{s.value}</div>
                    <div className="text-xs font-mono-custom tracking-wide uppercase" style={{ color: '#6A6A6A' }}>{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap gap-4">
                <a href="/packages" className="cta-gold px-7 py-3.5 rounded-sm text-sm font-semibold tracking-wide inline-flex items-center gap-2">
                  Work With Betty
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </a>
                <a href="/process" className="cta-secondary text-sm font-medium">
                  Learn More About Us →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}